import React, { useState, useMemo, useCallback } from 'react';
import { Alert, Button } from 'antd';
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  UserOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import {
  fetchDashboardData,
  DashboardData,
} from '../services/dashboardService';
import {
  getPresetRange,
  toQueryRange,
  formatRangePreview,
  DEFAULT_PRESET,
} from '../utils/dateRanges';
import {
  DASHBOARD_FOUNDER_QUERY_OPTIONS,
  dashboardFounderQueryKey,
} from '../config/queryConfig';
import type { DateRangeFilterValue } from './dashboard/DateRangeFilter';
import { DashboardHeader } from './dashboard/DashboardHeader';
import { KpiGrid, type KpiItem } from './dashboard/KpiGrid';
import { AnalyticsSection } from './dashboard/AnalyticsSection';
import { RecentActivitySection } from './dashboard/RecentActivitySection';
import OrderStatusDistributionChart from './dashboard/OrderStatusDistributionChart';
import PaymentMethodBreakdownChart from './dashboard/PaymentMethodBreakdownChart';
import { DashboardSkeleton } from './dashboard/DashboardSkeleton';
import { Row, Col } from 'antd';
import { useDashboardFounder } from '../hooks/useDashboardFounder';
import {
  OrdersGmvTrendChart,
  CancelRateTrendChart,
  OrderFunnelChart,
  FulfillmentTimeChart,
  TopMerchantsTable,
  MerchantRiskAlerts,
} from './dashboardFounder';

const getInitialDateRange = (): DateRangeFilterValue => {
  const range = getPresetRange(DEFAULT_PRESET);
  const query = toQueryRange(range.from, range.to);
  return {
    startDate: query.startDate,
    endDate: query.endDate,
    presetId: DEFAULT_PRESET,
  };
};

const DEFAULT_DATA: DashboardData = {
  kpis: {
    totalRevenue: 0,
    totalOrders: 0,
    activeMerchants: 0,
    newUsers: 0,
    previousRevenue: undefined,
    previousOrders: undefined,
    previousActiveMerchants: undefined,
    previousNewUsers: undefined,
  },
  revenueTrend: [],
  orderStatusBreakdown: {
    pending: 0,
    confirmed: 0,
    preparing: 0,
    ready: 0,
    delivering: 0,
    delivered: 0,
    cancelled: 0,
  },
  dailyOrderStatus: [],
  topMerchants: [],
  recentOrders: [],
  paymentMethodBreakdown: {},
};

const DashboardNew: React.FC = () => {
  const [dateFilterValue, setDateFilterValue] =
    useState<DateRangeFilterValue>(getInitialDateRange);
  const queryClient = useQueryClient();

  const dateRange = useMemo(
    () => ({
      startDate: dateFilterValue.startDate,
      endDate: dateFilterValue.endDate,
    }),
    [dateFilterValue.startDate, dateFilterValue.endDate]
  );

  const dashboardFilters = useMemo(
    () => ({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }),
    [dateRange.startDate, dateRange.endDate]
  );

  const queryFn = useCallback(() => fetchDashboardData(dateRange), [dateRange]);

  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
  } = useQuery<DashboardData>({
    queryKey: dashboardFounderQueryKey('stats', dashboardFilters),
    queryFn,
    ...DASHBOARD_FOUNDER_QUERY_OPTIONS,
    placeholderData: keepPreviousData,
  });

  const founderFilters = useMemo(
    () => ({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      compareWithPrevious: false,
    }),
    [dateRange.startDate, dateRange.endDate]
  );
  const { data: founderData, isLoading: founderLoading } =
    useDashboardFounder(founderFilters);

  const handleDateFilterChange = useCallback(
    (newValue: DateRangeFilterValue) => {
      queryClient.cancelQueries({ queryKey: ['dashboard-founder'] });
      queryClient.removeQueries({
        queryKey: ['dashboard-founder'],
        exact: false,
      });
      setDateFilterValue(newValue);
    },
    [queryClient]
  );

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard-founder'] });
  }, [queryClient]);

  const chartPeriod = dateFilterValue.presetId === 'today' ? 'today' : 'custom';

  const revenueSparkline = useMemo(() => {
    if (!dashboardData?.revenueTrend || dashboardData.revenueTrend.length === 0)
      return undefined;
    return dashboardData.revenueTrend.map(d => d.revenue);
  }, [dashboardData?.revenueTrend]);

  const ordersSparkline = useMemo(() => {
    if (!dashboardData?.revenueTrend || dashboardData.revenueTrend.length === 0)
      return undefined;
    return dashboardData.revenueTrend.map(d => d.orders);
  }, [dashboardData?.revenueTrend]);

  const data = dashboardData ?? DEFAULT_DATA;

  const kpiItems: KpiItem[] = useMemo(
    () => [
      {
        title: 'Total Revenue',
        value: data.kpis.totalRevenue,
        previousValue: data.kpis.previousRevenue,
        prefix: <DollarOutlined />,
        isCurrency: true,
        sparklineData: revenueSparkline,
      },
      {
        title: 'Total Orders',
        value: data.kpis.totalOrders,
        previousValue: data.kpis.previousOrders,
        prefix: <ShoppingCartOutlined />,
        sparklineData: ordersSparkline,
      },
      {
        title: 'Active Merchants',
        value: data.kpis.activeMerchants,
        previousValue: data.kpis.previousActiveMerchants,
        prefix: <ShopOutlined />,
      },
      {
        title: 'Active Users',
        value: data.kpis.newUsers,
        previousValue: data.kpis.previousNewUsers,
        prefix: <UserOutlined />,
      },
    ],
    [
      data.kpis.totalRevenue,
      data.kpis.previousRevenue,
      data.kpis.totalOrders,
      data.kpis.previousOrders,
      data.kpis.activeMerchants,
      data.kpis.previousActiveMerchants,
      data.kpis.newUsers,
      data.kpis.previousNewUsers,
      revenueSparkline,
      ordersSparkline,
    ]
  );

  if (isLoading && !dashboardData) {
    return (
      <div className="dashboard-page">
        <DashboardSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="dashboard-page">
        <section className="dashboard-section">
          <Alert
            message="Không tải được dữ liệu"
            description={
              error instanceof Error
                ? error.message
                : 'Không thể tải dữ liệu dashboard. Vui lòng thử lại.'
            }
            type="error"
            showIcon
            action={
              <Button onClick={handleRefresh} size="small">
                Thử lại
              </Button>
            }
            className="dashboard-error-alert"
          />
        </section>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Section A: Header / Controls */}
      <DashboardHeader
        title="Dashboard Overview"
        subtitle="Tổng quan hiệu suất và hoạt động"
        dateFilterValue={dateFilterValue}
        onDateFilterChange={handleDateFilterChange}
        loading={isLoading}
      />

      {/* Section B: KPI Cards */}
      <KpiGrid items={kpiItems} loading={isLoading} />

      {/* Section C: Key Trends - GMV & Orders */}
      <AnalyticsSection
        revenueTrend={data.revenueTrend}
        topMerchants={data.topMerchants}
        chartPeriod={chartPeriod}
        rangeLabel={formatRangePreview(
          new Date(dateRange.startDate),
          new Date(dateRange.endDate)
        )}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        loading={isLoading}
      />

      {/* Section D: Marketplace Insights - Status & Payment & Founder Charts */}
      <section className="dashboard-section dashboard-insights-section">
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12} xl={12}>
            <OrderStatusDistributionChart
              data={data.orderStatusBreakdown}
              rangeLabel={formatRangePreview(
                new Date(dateRange.startDate),
                new Date(dateRange.endDate)
              )}
              loading={isLoading}
            />
          </Col>
          <Col xs={24} md={12} xl={12}>
            <PaymentMethodBreakdownChart
              data={data.paymentMethodBreakdown}
              rangeLabel={formatRangePreview(
                new Date(dateRange.startDate),
                new Date(dateRange.endDate)
              )}
              loading={isLoading}
            />
          </Col>
          {/* <Col xs={24} md={12} xl={8}>
            <RevenueBreakdownChart
              data={founderData?.trendDaily ?? []}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              loading={founderLoading}
            />
          </Col> */}
        </Row>
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={24}>
            <OrdersGmvTrendChart
              data={founderData?.trendDaily ?? []}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              loading={founderLoading}
            />
          </Col>
        </Row>
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} md={12} lg={8}>
            <CancelRateTrendChart
              data={founderData?.trendDaily ?? []}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              loading={founderLoading}
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <OrderFunnelChart
              data={
                founderData?.funnel ?? {
                  created: 0,
                  confirmed: 0,
                  delivered: 0,
                  cancelled: 0,
                }
              }
              loading={founderLoading}
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <FulfillmentTimeChart
              data={founderData?.trendDaily ?? []}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              loading={founderLoading}
            />
          </Col>
        </Row>
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={14}>
            <TopMerchantsTable
              data={founderData?.topMerchants ?? []}
              loading={founderLoading}
            />
          </Col>
          <Col xs={24} lg={10}>
            <MerchantRiskAlerts
              alerts={founderData?.alerts ?? []}
              loading={founderLoading}
            />
          </Col>
        </Row>
      </section>

      {/* Section E: Operations - Latest Transactions */}
      <RecentActivitySection data={data.recentOrders} loading={isLoading} />
    </div>
  );
};

export default DashboardNew;
